/**
 * 图片预览弹窗 — 全屏遮罩 + 可缩放大图 + 左右切换 + ESC关闭
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ImageAsset } from '../services/mediaApi';
import { stripExt } from '../utils/format';
import ZoomableImage from './ZoomableImage';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

interface Props {
  image: ImageAsset;
  images: ImageAsset[]; // 同屏图片列表（用于左右切换）
  onClose: () => void;
}

const ImagePreviewModal: React.FC<Props> = ({ image, images, onClose }) => {
  const startIdx = Math.max(0, images.findIndex(i => i.id === image.id));
  const [curIdx, setCurIdx] = useState(startIdx);
  const current = images[curIdx] ?? image;

  const goTo = useCallback((dir: -1 | 1) => {
    setCurIdx(prev => {
      const ni = prev + dir;
      return ni >= 0 && ni < images.length ? ni : prev;
    });
  }, [images.length]);

  const hasPrev = curIdx > 0;
  const hasNext = curIdx < images.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) goTo(-1);
      if (e.key === 'ArrowRight' && hasNext) goTo(1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, goTo, hasPrev, hasNext]);

  const imgUrl = `${API_BASE}/${current.file_path}`;

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300 z-10">✕</button>

      {/* 上一张 */}
      {hasPrev && (
        <button
          onClick={e => { e.stopPropagation(); goTo(-1); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl hover:text-blue-300 z-10 opacity-60 hover:opacity-100 transition-opacity"
        >←</button>
      )}

      <div className="w-[90vw] h-[80vh]" onClick={e => e.stopPropagation()}>
        <ZoomableImage src={imgUrl} alt={current.original_name} />
      </div>

      {/* 下一张 */}
      {hasNext && (
        <button
          onClick={e => { e.stopPropagation(); goTo(1); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl hover:text-blue-300 z-10 opacity-60 hover:opacity-100 transition-opacity"
        >→</button>
      )}

      <div className="text-white text-sm mt-4 flex items-center gap-2">
        <span>{stripExt(current.original_name)}</span>
        {current.width && current.height && (
          <span className="text-gray-400 text-xs">{current.width}×{current.height}</span>
        )}
        {current.ai_description && (
          <span className="text-gray-400 text-xs ml-2 max-w-md truncate">— {current.ai_description}</span>
        )}
      </div>
      <div className="text-gray-500 text-xs mt-1">
        {curIdx + 1} / {images.length}
      </div>
    </div>
  );
};

export default ImagePreviewModal;
